import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import { Search, Plus, Edit2, Trash2, UserCircle, ShieldCheck, Circle } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];
    users.forEach((u) => {
      const idx = state.findIndex((x) => x.id === u.id);
      if (idx !== -1) state[idx] = u; else newUsers.push(u);
    });
    return [...state, ...newUsers];
  }
  if (action.type === "UPDATE_USERS") {
    const u = action.payload;
    const idx = state.findIndex((x) => x.id === u.id);
    if (idx !== -1) { state[idx] = { ...state[idx], ...u }; return [...state]; }
    return [u, ...state];
  }
  if (action.type === "DELETE_USER") return state.filter((u) => u.id !== action.payload);
  if (action.type === "RESET") return [];
  return state;
};

const getInitials = (name) => {
  if (!name) return "";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().substring(0, 2);
};

const getTimeAgo = (date) => {
  if (!date) return "Nunca";
  const now = new Date();
  const d = new Date(date);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "Ahora mismo";
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)}h`;
  return `Hace ${Math.floor(diff / 86400)}d`;
};

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) { toastError(err); }
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    const handleUser = (data) => {
      if (data.action === "update" || data.action === "create")
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      if (data.action === "delete")
        dispatch({ type: "DELETE_USER", payload: +data.userId });
    };

    const handleUserStatus = (data) => {
      dispatch({
        type: "UPDATE_USERS",
        payload: { id: data.userId, isOnline: data.isOnline, lastSeenAt: data.lastSeenAt }
      });
    };

    socket.on("user", handleUser);
    socket.on("userStatus", handleUserStatus);

    return () => {
      socket.off("user", handleUser);
      socket.off("userStatus", handleUserStatus);
    };
  }, []);

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) { toastError(err); }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingUser ? `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?` : ""}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={() => { setSelectedUser(null); setUserModalOpen(false); }}
        userId={selectedUser?.id}
      />

      <MainHeader>
        <Title>{i18n.t("users.title")}</Title>
        <MainHeaderButtonsWrapper>
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              placeholder={i18n.t("contacts.searchPlaceholder")}
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
              className="pl-9 pr-4 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full sm:w-52 transition-all"
            />
          </div>
          <button
            onClick={() => { setSelectedUser(null); setUserModalOpen(true); }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            {i18n.t("users.buttons.add")}
          </button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6" onScroll={handleScroll}>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                {["Estado", i18n.t("users.table.name"), i18n.t("users.table.email"), "Rol", i18n.t("users.table.whatsapp"), "Ultima conexion", i18n.t("users.table.actions")].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap ${i === 1 ? "text-left" : "text-center"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {users.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <p className="text-base font-semibold text-slate-500 dark:text-slate-400">No hay usuarios</p>
                    <p className="text-sm text-slate-400 mt-1">Agrega el primer usuario para comenzar</p>
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center" title={u.isOnline ? "En linea" : `Ultimo visto: ${getTimeAgo(u.lastSeenAt || u.lastLoginAt)}`}>
                        <Circle className={`w-3 h-3 ${u.isOnline ? "fill-emerald-500 text-emerald-500" : "fill-slate-300 text-slate-300 dark:fill-slate-600 dark:text-slate-600"}`} />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {getInitials(u.name)}
                        </div>
                        <span className="font-medium text-slate-900 dark:text-slate-100">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-500 dark:text-slate-400 whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        u.profile === "admin"
                          ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                          : "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
                      }`}>
                        {u.profile === "admin" ? <ShieldCheck className="w-3 h-3" /> : <UserCircle className="w-3 h-3" />}
                        {u.role?.name || u.profile}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <div className="flex flex-col items-center gap-1">
                        {u.whatsapp?.name ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" title="Predeterminada">
                            Predet: {u.whatsapp.name}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                        {u.whatsapps && u.whatsapps.length > 0 && (
                          <span className="text-[10px] text-slate-500 dark:text-slate-400">
                            Permitidas: {u.whatsapps.map(w => w.name).join(", ")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {u.isOnline ? (
                        <span className="text-emerald-600 dark:text-emerald-400 font-medium">En linea</span>
                      ) : (
                        getTimeAgo(u.lastSeenAt || u.lastLoginAt)
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setSelectedUser(u); setUserModalOpen(true); }} title="Editar" className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => { setConfirmModalOpen(true); setDeletingUser(u); }} title="Eliminar" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {loading && (
                <tr>
                  <td colSpan={7} className="py-4 text-center">
                    <div className="flex justify-center"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MainContainer>
  );
};

export default Users;
